import PropTypes from 'prop-types'
import { ResponsivePie } from '@nivo/pie'
import { useTheme } from '@emotion/react'
import { Section } from 'chaoskit/src/components'
import { Fragment } from 'react'

import commonProperties from './common.json'
import { getChartColors, getDonutVariants } from './utilities'
import { ContentGrid } from './Content'
import GraphLegend from './GraphLegend'

import Data from '~components/Data'

export default function DonutGraph({ dataset, graphProps = {}, ...rest }) {
  return (
    <div
      css={{
        height: 350,
      }}
      {...rest}
    >
      <ResponsivePie
        {...commonProperties}
        colors={getChartColors(dataset.length)}
        data={dataset}
        innerRadius={0.6}
        padAngle={1}
        enableRadialLabels={false}
        sliceLabelsTextColor={{
          from: 'color',
          modifiers: [['brighter', 6]],
        }}
        {...graphProps}
      />
    </div>
  )
}

DonutGraph.propTypes = {
  dataset: PropTypes.array.isRequired,
  graphProps: PropTypes.object,
}

export function DonutSection({ title, graphProps = {}, dataset, type = '' }) {
  const theme = useTheme()

  const typeProps = getDonutVariants(type)

  return (
    <Section space="base">
      <ContentGrid
        css={{
          alignItems: 'center',
        }}
      >
        <div
          css={{
            fontSize: theme.fontSize.large,
            textAlign: 'center',
            position: 'relative',
            zIndex: 1, // So it sits above overlapping graph

            [theme.mq.medium]: {
              fontSize: theme.fontSize.xlarge,
              textAlign: 'right',
            },
          }}
        >
          <Data>{title}</Data>
        </div>
        <div
          css={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: theme.space.large,
            alignItems: 'center',

            [theme.mq.medium]: {
              gridTemplateColumns: '2fr 1fr',
            },
          }}
        >
          <DonutGraph
            dataset={dataset}
            graphProps={{ ...typeProps, ...graphProps }}
          />
          <GraphLegend
            dataset={dataset}
            desktopVertical
            css={{
              marginTop: theme.space.base,

              [theme.mq.medium]: {
                marginTop: 0,
              },
            }}
          />
        </div>
      </ContentGrid>
    </Section>
  )
}

DonutSection.propTypes = {
  dataset: PropTypes.array.isRequired,
  graphProps: PropTypes.object,
  title: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['donut', 'donut-percentage']),
}

export function StackedDonutSection({ graphs }) {
  const theme = useTheme()

  return (
    <Fragment>
      <div
        css={{
          width: '100vw',
          position: 'relative',
          left: '50%',
          right: '50%',
          marginLeft: '-50vw',
          marginRight: '-50vw',
          maskImage:
            'linear-gradient(90deg, transparent, #000 7.5%, #000 92.5%, transparent)',
          overflowX: 'auto',
          scrollbarWidth: 'none',
          MsOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch',
          marginTop: theme.space.large,
          marginBottom: theme.space.large,

          '&::-webkit-scrollbar': {
            width: 0,
            height: 0,
          },
        }}
      >
        <div
          css={{
            display: 'grid',
            gridAutoColumns: 'auto',
            gridAutoFlow: 'column',
            gap: theme.space.base,
            marginLeft: theme.space.large,
            marginRight: theme.space.large,
          }}
        >
          {graphs.map((graph) => {
            const typeProps = getDonutVariants(graph.type)
            return (
              <div
                key={graph.heading}
                css={{
                  display: 'grid',
                  gridTemplateColumns: '1fr',
                  gap: theme.space.base,
                  width: 400 /* A bit larger than donut height to give room for labels */,
                  flex: 'none',
                }}
              >
                <div
                  css={{
                    fontSize: theme.fontSize.large,
                    textAlign: 'center',
                  }}
                >
                  {graph.heading}
                </div>
                <DonutGraph dataset={graph.data} graphProps={typeProps} />
              </div>
            )
          })}
          <div css={{ width: 1 }} />
        </div>
      </div>
      <GraphLegend dataset={graphs[0].data} />
    </Fragment>
  )
}

StackedDonutSection.propTypes = {
  graphs: PropTypes.array.isRequired,
}
